import { IonContent, IonLoading, IonPage } from '@ionic/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';
import { useBeaconRouter } from '../../route/route.hooks';
import { getDirectConnectHealth } from '../api/direct-connect-health-api';
import { getDirectConnectRegistration } from '../api/direct-connect-registration-api';
import { DirectConnectCacheConnectedMachine } from '../gate/direct-connect-cache-machine-gate';
import {
  DirectConnectHealthStatus,
  DirectConnectRegistrationResponse,
} from '../model/direct-connect.model';

interface DirectConnectRegistrationProps {
  url: string;
}

const DirectConnectRegistration = (props: DirectConnectRegistrationProps) => {
  const { url } = props;

  const [registration, setRegistration] = useState<
    DirectConnectRegistrationResponse | undefined
  >(undefined);

  const [healthStatus, setHealthStatus] = useState<
    DirectConnectHealthStatus | undefined
  >(undefined);

  const { i18n } = useTranslation();
  const { builder } = useBeaconRouter();

  const startOrCheckDirectConnectRegistration = useCallback(
    async (url: string) => {
      try {
        const health = await getDirectConnectHealth(url);

        if (health && !health?.isRegistered) {
          const response = await getDirectConnectRegistration(url);

          if (response) {
            setRegistration(response);
          }
        }

        setHealthStatus(health);
      } catch (err) {
        // ignore failure since CC will restart after registration complete
      }
    },
    []
  );

  const registeredRedirectUrl = useMemo(() => {
    const machineId = healthStatus?.machine?.machineId;

    if (healthStatus?.isRegistered) {
      if (machineId) {
        return builder.getDirectConnectMachineRoute(machineId);
      }
    }
  }, [builder, healthStatus]);

  useEffect(() => {
    const interval = setInterval(() => {
      startOrCheckDirectConnectRegistration(url);
    }, 5_000);

    // kick off initial registration
    startOrCheckDirectConnectRegistration(url);

    return () => {
      clearInterval(interval);
    };
  }, [url, startOrCheckDirectConnectRegistration]);

  return registeredRedirectUrl ? (
    <DirectConnectCacheConnectedMachine healthStatus={healthStatus}>
      <Redirect to={registeredRedirectUrl} />
    </DirectConnectCacheConnectedMachine>
  ) : (
    <>
      <IonLoading
        isOpen={!registration}
        message={i18n.t('common:initializing_registration')}
      />
      {registration && (
        <IonPage>
          <IonContent>
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <h1>{registration.id}</h1>
            </div>
          </IonContent>
        </IonPage>
      )}
    </>
  );
};

export { DirectConnectRegistration };
